import React from "react";
import ViewCard from "../Components/Analytics/ViewCard.tsx";
import Trends from "../Components/Analytics/Trends.tsx";
import AverageTime from "../Components/Analytics/AverageTime.tsx";
import BrowserOsDevice from "../Components/Analytics/BrowserOsDevice.tsx";
import WorldMap from "../Components/Analytics/WorldMap.tsx";
import { useFormInsightsContext } from "../Contexts/FormInsightsContext.tsx";
import FormResponsesView from "./FormResponsesView";
import CustomFunnel from "../Components/Analytics/CustomFunnel.tsx";
import QuestionInsightDataTable from "../Components/Analytics/QuestionInsightDataTable.tsx";
import ContactsDataTable from "../Components/Analytics/ContactsDataTable.tsx";
import { useParams, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery, useQueries } from "@tanstack/react-query";
import { useApi } from "../Api/useApi.ts";
import { ErrorBoundary } from "react-error-boundary";
import {
    Play,
    CircleCheckBig,
    CirclePercent,
    Clock2,
    Contrast,
} from "lucide-react";

const chartIds = [
    "trend_submissions_average",
    "trend_time_to_complete",
    "geo_location",
    "trend_submissions",
    "funnel",
];
const piechartIds = ["devices_pie_chart", "os_pie_chart", "browser_pie_chart"];

function FormAnalyticsView() {
    const { analyticsTab, activeTab } = useFormInsightsContext();
    return (
        <div className="flex flex-col flex-1 gap-3 overflow-hidden">
            {activeTab === "analytics" &&
                (analyticsTab === "overview" ? (
                    <FormOverView />
                ) : analyticsTab === "insights" ? (
                    <QuestionInsight />
                ) : analyticsTab === "contacts" ? (
                    <Contacts />
                ) : null)}
            {activeTab === "submissions" && <FormResponsesView />}
        </div>
    );
}

export default FormAnalyticsView;

const FormOverView = () => {
    const api = useApi();
    const { urlId, workspaceId } = useParams();
    const [searchParams] = useSearchParams();
    const from = searchParams.get("from");
    const to = searchParams.get("to");

    const {
        data: overviewStats,
        isSuccess: isOverviewSuccess,
        isFetching: isOverviewFetching,
        isError: isOverviewError,
    } = useQuery({
        queryKey: ["overviewStats", urlId, workspaceId, from, to],
        queryFn: () =>
            api.analytics.getOverview(
                urlId as string,
                workspaceId as string,
                from as string,
                to as string,
            ),
        staleTime: Infinity,
        retry: 1,
        placeholderData: keepPreviousData,
    });

    const chartQueries = useQueries({
        queries: chartIds?.map((chartId) => ({
            queryKey: ["chartStats", urlId, workspaceId, from, to, chartId],
            queryFn: () =>
                api.analytics.getCharts(
                    urlId as string,
                    workspaceId as string,
                    chartId as string,
                    from as string,
                    to as string,
                ),
            staleTime: Infinity,
            placeholderData: keepPreviousData,
            retry: false,
        })),
    });
    const pieChartQueries = useQueries({
        queries: piechartIds?.map((chartId) => ({
            queryKey: ["piehartStats", urlId, workspaceId, from, to, chartId],
            queryFn: () =>
                api.analytics.getPies(
                    urlId as string,
                    workspaceId as string,
                    chartId as string,
                    from as string,
                    to as string,
                ),
            staleTime: Infinity,
            placeholderData: keepPreviousData,
            retry: false,
        })),
    });

    const chartData = {
        trendSubmissionsAverage: chartQueries[0]?.data,
        trendTimeToComplete: chartQueries[1]?.data,
        geoLocation: chartQueries[2]?.data,
        trendSubmissions: chartQueries[3]?.data,
        funnel: chartQueries[4]?.data,
    };

    const chartDataErrors = {
        trendSubmissionsAverage: chartQueries[0]?.status,
        trendTimeToComplete: chartQueries[1]?.status,
        geoLocation: chartQueries[2]?.status,
        trendSubmissions: chartQueries[3]?.status,
        funnel: chartQueries[4]?.status,
    };

    const pieChartData = {
        devices: pieChartQueries[0]?.data,
        os: pieChartQueries[1]?.data,
        browser: pieChartQueries[2]?.data,
    };

    const pieChartDataErrors = {
        devices: pieChartQueries[0]?.status,
        os: pieChartQueries[1]?.status,
        browser: pieChartQueries[2]?.status,
    };

    const secondsToMinutes = (
        seconds: number,
    ): { minutes: number; seconds: number } => {
        if (!seconds) return { minutes: 0, seconds: 0 };
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.ceil(seconds % 60);
        return { minutes, seconds: remainingSeconds };
    };

    return (
        <div className="flex-1 w-full py-4 overflow-y-auto no-scrollbar">
            <div className="flex flex-col w-full gap-3">
                <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <ViewCard
                        title="Start"
                        count={overviewStats?.data?.started_count}
                        icon={<Play size={16} />}
                    />
                    <ViewCard
                        title="Partial"
                        count={overviewStats?.data?.partial_count}
                        icon={<Contrast size={16} />}
                    />
                    <ViewCard
                        title="Submissions"
                        count={overviewStats?.data?.completed_count}
                        icon={<CircleCheckBig size={16} />}
                    />
                    <ViewCard
                        title="Completion rate"
                        count={overviewStats?.data?.completion_rate?.toFixed(2)}
                        icon={<CirclePercent size={16} />}
                        percentage
                    />
                    <ViewCard
                        title="Time to complete"
                        isTime
                        min={
                            secondsToMinutes(
                                overviewStats?.data?.average_time_to_complete,
                            ).minutes
                        }
                        sec={
                            secondsToMinutes(
                                overviewStats?.data?.average_time_to_complete,
                            ).seconds
                        }
                        icon={<Clock2 size={16} />}
                        styles="col-span-2 md:col-span-1"
                    />
                </section>
                <ErrorBoundary fallback={<NoDataError height={332} />}>
                    <Trends
                        trends={chartData?.trendSubmissions}
                        trendsErrors={chartDataErrors?.trendSubmissions}
                    />
                </ErrorBoundary>
                <ErrorBoundary fallback={<NoDataError height={400} />}>
                    <CustomFunnel
                        funnelData={chartData?.funnel}
                        funnelError={chartDataErrors?.funnel}
                    />
                </ErrorBoundary>

                <AverageTime
                    submissionAverage={chartData?.trendSubmissionsAverage}
                    timetoComplete={chartData?.trendTimeToComplete}
                    submissionAverageErrors={
                        chartDataErrors?.trendSubmissionsAverage
                    }
                    timetoCompleteErrors={chartDataErrors?.trendTimeToComplete}
                />

                <BrowserOsDevice
                    devices={pieChartData?.devices}
                    os={pieChartData?.os}
                    browser={pieChartData?.browser}
                    devicesErrors={pieChartDataErrors?.devices}
                    osErrors={pieChartDataErrors?.os}
                    browserErrors={pieChartDataErrors?.browser}
                />

                <ErrorBoundary fallback={<NoDataError height={400} />}>
                    <WorldMap
                        geoLocationData={chartData?.geoLocation}
                        geoLocationErrors={chartDataErrors?.geoLocation}
                    />
                </ErrorBoundary>
            </div>
        </div>
    );
};

const QuestionInsight = () => {
    return (
        <section className="flex flex-col gap-3 mt-3">
            <div className="flex flex-col">
                <h1 className="text-base font-semibold text-[#191B33]">
                    Question Insights
                </h1>
                <p className="text-sm font-normal text-gray-600">
                    In-depth question by question submission insights
                </p>
            </div>
            <QuestionInsightDataTable />
        </section>
    );
};

const Contacts = () => {
    return (
        <section className="flex flex-col gap-3 mt-3">
            <div className="flex flex-col">
                <h1 className="text-base font-semibold text-[#191B33]">
                    Unique Contacts
                </h1>
                <p className="text-sm font-normal text-gray-600">
                    Over{" "}
                    <span className="font-semibold text-gray-950"> 140</span>{" "}
                    contacts filled your form
                </p>
            </div>
            <ContactsDataTable />
        </section>
    );
};

export const NoDataError = ({ height }: { height?: number }) => {
    return (
        <div
            className="flex flex-col items-center justify-center h-full"
            style={{ height: height }}
        >
            <svg
                width="40"
                height="41"
                viewBox="0 0 40 41"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g clip-path="url(#clip0_6499_27977)">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M40 9.63295L39.8333 6.84961C39.7663 6.3313 39.6604 5.81875 39.5167 5.31628C39.3871 4.8783 39.1718 4.47041 38.8833 4.11628C38.4063 3.71639 37.8307 3.45166 37.2167 3.34961C35.5644 3.04405 33.8943 2.84364 32.2167 2.74961C26.8 2.38295 19.5 2.43295 17.8 2.46628C15.3167 2.56628 12.9167 2.71628 10.5 2.89961C8.08333 3.08295 5.7 3.33295 3.23333 3.64961C3.10532 3.68098 2.99283 3.75726 2.91634 3.86459C2.83984 3.97191 2.80443 4.10313 2.81654 4.23437C2.82866 4.36561 2.88749 4.48813 2.98234 4.57964C3.07719 4.67114 3.20175 4.72555 3.33333 4.73295C5.78333 4.53295 8.16667 4.38295 10.5667 4.28295C12.9667 4.18295 15.35 4.16628 17.8 4.18295C19.2833 4.18295 25.0167 4.38295 30.0167 4.69961C32.5833 4.86628 35.0167 5.04961 36.4167 5.31628C36.9167 5.31628 37.5833 5.31628 37.4833 5.58295C37.5825 5.74857 37.6555 5.92845 37.7 6.11628C37.8 6.48295 37.85 6.84961 37.9167 7.18295L38.1333 9.66628L38.25 13.2829L38.3333 19.4829V23.5496C38.3333 25.3329 38.1333 27.0996 38.0167 28.8829C37.9893 28.7448 37.9142 28.6206 37.8045 28.5323C37.6948 28.4439 37.5575 28.3969 37.4167 28.3996L21.8833 28.6663C19.15 28.7496 16.4 28.9496 13.6667 28.9996H10.2167L3.85 28.8329C3.45339 28.8105 3.05834 28.766 2.66667 28.6996C2.48333 28.6996 2.28333 28.6163 2.18333 28.4663C2.04487 28.1877 1.98714 27.876 2.01667 27.5663C2.01667 26.7996 2.1 25.8996 2.1 25.4163H2.3C6.55 25.4163 10.8333 25.4163 15.15 25.3163C17.2667 25.3163 19.4 25.3163 21.5333 25.1996C23.6667 25.0829 25.8 25.0829 27.9333 25.0663C30.0667 25.0496 32.2 25.0663 34.3333 25.0663C34.4143 25.0708 34.4954 25.0592 34.572 25.0323C34.6485 25.0054 34.719 24.9637 34.7794 24.9095C34.8398 24.8553 34.8889 24.7897 34.9239 24.7166C34.9589 24.6434 34.9791 24.564 34.9833 24.4829C34.9902 24.4014 34.9803 24.3194 34.9542 24.2418C34.9282 24.1643 34.8866 24.0929 34.832 24.032C34.7773 23.9711 34.7108 23.9221 34.6366 23.8879C34.5623 23.8536 34.4818 23.8349 34.4 23.8329C30.6667 23.4829 26.9167 23.2329 23.15 23.1496H18.3C16.6833 23.1496 15.0667 23.1496 13.4667 23.3163C9.7 23.5329 5.96667 23.9329 2.25 24.2996H2.1V15.2996C2.05476 13.3085 2.12153 11.3165 2.3 9.33295C2.4082 8.02716 2.67689 6.73968 3.1 5.49961C3.14998 5.36677 3.14667 5.21975 3.09076 5.08929C3.03484 4.95883 2.93067 4.85504 2.8 4.79961C2.66275 4.74994 2.51163 4.75514 2.37812 4.81413C2.24461 4.87312 2.13901 4.98136 2.08333 5.11628C1.57857 6.42597 1.23168 7.79116 1.05 9.18295C0.799508 11.235 0.660415 13.2992 0.633333 15.3663L0.15 25.2663C0.0529295 26.1575 0.00286011 27.0532 0 27.9496C0.0207614 28.4953 0.175291 29.0276 0.45 29.4996C0.696189 29.8863 1.04938 30.1932 1.46667 30.3829C2.15976 30.6469 2.89204 30.7934 3.63333 30.8163C4.28333 30.8163 4.95 30.933 5.61667 30.9496C7.1 30.9496 8.58333 30.9496 10.0833 31.0496C11.5833 31.1496 13.2833 31.0496 14.8833 31.0496L16.3833 30.933L14.3333 35.3663C14.0455 35.8753 13.7948 36.4044 13.5833 36.9496C13.4943 37.2203 13.4943 37.5123 13.5833 37.783C13.6433 37.926 13.7341 38.054 13.8494 38.1577C13.9646 38.2615 14.1015 38.3384 14.25 38.3829C14.4864 38.4398 14.7256 38.4843 14.9667 38.5163C15.2995 38.5413 15.6338 38.5413 15.9667 38.5163C16.4354 38.4965 16.9027 38.452 17.3667 38.3829C19.02 38.0843 20.6896 37.8839 22.3667 37.783C26.1833 37.5663 26.9833 38.0496 26.05 34.5496C25.9333 34.1163 25.8333 33.6996 25.6833 33.2829C25.5333 32.8663 25.3667 32.4496 25.1833 32.0663C24.85 31.433 24.4833 30.8663 24.1167 30.2996L37.45 29.5996C37.5755 29.5921 37.6953 29.5443 37.7914 29.4632C37.8875 29.3821 37.9548 29.2721 37.9833 29.1496V29.683C37.9739 29.8456 38.0275 30.0057 38.133 30.1299C38.2386 30.254 38.3879 30.3327 38.55 30.3496C38.7153 30.3588 38.8777 30.3029 39.0023 30.1939C39.1269 30.0849 39.2038 29.9314 39.2167 29.7663C39.4 27.6996 39.6333 25.6663 39.8 23.5996C39.9 22.2163 39.9833 20.8329 40.0167 19.4329V9.63295H40ZM23.4333 33.2163C23.6333 33.8663 23.8833 34.483 24.1333 35.1163C24.3833 35.7496 24.5 36.033 24.7 36.483C23.6294 36.3169 22.5496 36.2168 21.4667 36.1829C20.0144 36.1612 18.562 36.2224 17.1167 36.3663H15.8833C15.9685 36.2432 16.0464 36.1152 16.1167 35.983C16.5167 34.633 16.85 33.283 17.2667 31.9496C17.4 31.533 17.55 31.1163 17.7 30.7163C19.1167 30.5996 20.5333 30.4663 21.95 30.3829H22.7C22.9333 31.333 23.1333 32.2663 23.4333 33.2163Z"
                        fill="#2563EB"
                    />
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M11.1499 14.1994C10.9332 14.516 10.7166 14.816 10.5332 15.1494C10.3499 15.4827 9.89988 16.2827 9.58322 16.816C9.36513 16.7199 9.13541 16.6526 8.89988 16.616C8.36885 16.5695 7.83481 16.6613 7.34988 16.8827C6.94613 17.0382 6.58477 17.2868 6.29517 17.6083C6.00557 17.9297 5.7959 18.315 5.68322 18.7327C5.41655 19.9494 5.93322 21.416 8.09988 21.3327C10.7999 21.566 11.2332 20.0827 11.3332 19.316C11.3607 18.9844 11.3169 18.6507 11.2048 18.3374C11.0927 18.0241 10.9148 17.7383 10.6832 17.4994C11.2821 17.0498 11.8399 16.5478 12.3499 15.9994C12.7432 15.5193 13.0944 15.0063 13.3999 14.466C13.8666 13.666 14.2332 12.7994 14.6666 12.016C14.7068 11.9299 14.7276 11.8361 14.7276 11.741C14.7276 11.646 14.7068 11.5521 14.6666 11.466C15.0612 11.5584 15.4719 11.5584 15.8666 11.466C16.3171 12.0692 16.8251 12.6274 17.3832 13.1327C17.8028 13.4829 18.2486 13.8005 18.7165 14.0827C19.1332 14.3494 19.5666 14.5994 19.9832 14.8327C19.5868 15.1705 19.2972 15.6165 19.1499 16.116C18.8999 17.3327 19.4165 18.7994 21.5665 18.716C24.2666 18.9494 24.6999 17.466 24.7999 16.6994C24.8372 16.3033 24.7621 15.9047 24.5832 15.5494C25.1166 15.266 25.6665 14.9827 26.1665 14.6494C26.7804 14.2374 27.3403 13.7503 27.8332 13.1994C28.4374 12.557 28.9944 11.8719 29.4999 11.1494C29.5538 11.0944 29.5963 11.0292 29.6249 10.9577C29.6535 10.8862 29.6677 10.8097 29.6665 10.7327C29.8164 10.742 29.9667 10.742 30.1166 10.7327C32.8332 10.966 33.2499 9.48271 33.3499 8.71605C33.3725 8.37297 33.3254 8.02887 33.2116 7.70443C33.0978 7.38 32.9195 7.08194 32.6875 6.82817C32.4555 6.57439 32.1746 6.37015 31.8617 6.22773C31.5488 6.0853 31.2103 6.00763 30.8666 5.99938C30.331 5.97045 29.7957 6.06156 29.2999 6.26605C28.9003 6.43302 28.5433 6.68734 28.2548 7.01036C27.9664 7.33337 27.754 7.71688 27.6332 8.13271C27.5085 8.59162 27.5487 9.07971 27.7466 9.5121C27.9445 9.94449 28.2877 10.2938 28.7165 10.4994C28.0499 10.9994 27.3499 11.416 26.6832 11.8994C26.0166 12.3827 25.7499 12.5994 25.3332 12.9827C24.8089 13.5061 24.319 14.0628 23.8666 14.6494C23.4502 14.3604 22.9696 14.1774 22.4665 14.116C22.1545 14.1266 21.8457 14.1827 21.5499 14.2827C21.1118 13.7203 20.6327 13.1911 20.1165 12.6994C19.8654 12.454 19.5982 12.2257 19.3166 12.016C19.0494 11.812 18.7712 11.6228 18.4832 11.4494C18.0332 11.1827 17.5832 10.9494 17.1499 10.716C17.403 10.3862 17.564 9.99512 17.6165 9.58271C17.6392 9.24045 17.592 8.89716 17.478 8.57365C17.364 8.25014 17.1855 7.95316 16.9533 7.7007C16.7211 7.44823 16.4401 7.24556 16.1272 7.10495C15.8143 6.96434 15.4762 6.88874 15.1332 6.88271C14.6013 6.85193 14.0698 6.94909 13.5832 7.16605C13.1776 7.32319 12.815 7.57432 12.5253 7.89882C12.2355 8.22332 12.0269 8.61192 11.9166 9.03271C11.8371 9.2982 11.8156 9.57767 11.8536 9.85218C11.8915 10.1267 11.9881 10.3898 12.1367 10.6238C12.2852 10.8577 12.4824 11.057 12.7147 11.208C12.947 11.3591 13.2091 11.4585 13.4832 11.4994C12.9166 12.0994 12.3332 12.6827 11.8166 13.3327C11.6666 13.5994 11.3832 13.8327 11.1499 14.1994ZM8.73322 20.0494C8.63105 20.0408 8.52828 20.0558 8.43289 20.0934C8.3375 20.131 8.25206 20.1901 8.18322 20.266C7.59988 20.1327 7.33322 19.8494 7.33322 19.516C7.33322 19.1827 7.53322 19.1994 7.69988 19.0494C7.85084 18.9218 8.02628 18.8264 8.21545 18.7691C8.40461 18.7117 8.60349 18.6937 8.79988 18.716C9.41655 18.716 9.79988 19.116 9.66655 19.4994C9.5966 19.6834 9.46606 19.8382 9.29641 19.9382C9.12676 20.0382 8.92813 20.0774 8.73322 20.0494ZM29.8832 8.59938C30.0325 8.47518 30.2056 8.38298 30.392 8.3285C30.5783 8.27403 30.7739 8.25844 30.9666 8.28271C31.5832 8.28271 31.9666 8.66605 31.8332 9.06605C31.7629 9.24887 31.6316 9.40181 31.4616 9.49899C31.2915 9.59617 31.0931 9.63163 30.8999 9.59938C30.7983 9.58816 30.6956 9.60381 30.602 9.64476C30.5084 9.68571 30.4272 9.75052 30.3665 9.83271C29.7665 9.69938 29.4999 9.41605 29.4999 9.08271C29.4999 8.74938 29.6999 8.83271 29.8832 8.59938ZM23.2166 16.9327C23.1466 17.1168 23.0161 17.2715 22.8464 17.3715C22.6768 17.4715 22.4781 17.5107 22.2832 17.4827C22.1829 17.464 22.0793 17.4745 21.9848 17.5129C21.8903 17.5513 21.8087 17.616 21.7499 17.6994C21.1499 17.566 20.8832 17.2827 20.8832 16.9494C20.8832 16.616 21.0999 16.6327 21.2666 16.466C21.5718 16.2219 21.9612 16.1081 22.3499 16.1494C22.9832 16.1827 23.3332 16.5994 23.2166 16.9827V16.9327ZM13.9332 9.28271C14.0809 9.16149 14.2514 9.07103 14.4346 9.01665C14.6177 8.96227 14.81 8.94506 14.9999 8.96605C15.6166 8.96605 15.9832 9.34938 15.8666 9.74938C15.7947 9.9168 15.6729 10.0579 15.5177 10.1534C15.3626 10.2489 15.1817 10.294 14.9999 10.2827C14.8924 10.2687 14.783 10.2811 14.6814 10.3188C14.5797 10.3566 14.4888 10.4185 14.4165 10.4994C13.8332 10.366 13.5499 10.0994 13.5666 9.76605C13.5832 9.43271 13.7666 9.43271 13.9332 9.28271Z"
                        fill="#2563EB"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_6499_27977">
                        <rect
                            width="40"
                            height="40"
                            fill="white"
                            transform="translate(0 0.5)"
                        />
                    </clipPath>
                </defs>
            </svg>
            <p className="mt-3 text-[16px] font-medium text-primary">
                Waiting for responses...
            </p>

            <p className="mt-1 text-xs text-center text-gray-500">
                Your insights will appear here as soon as data is collected.
            </p>
        </div>
    );
};
